import { Link } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@design-system';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import { getOauthProviderIcon } from '@client/util/getOauthProviderIcon';
import { useSignupForm } from '@client/hooks/useSignupForm';
import { Spinner } from '@design-system';
import { Loader2, Mail, Phone, Shield, AlertCircle } from 'lucide-react';

export function Signup() {
  const {
    form,
    onSubmit,
    handleSocialLogin,
    handlePhoneNumberChange,
    isChecking,
    isRedirecting,
    existingAdvisor,
    clearExistingAdvisorError,
    isPasswordMode,
  } = useSignupForm();
  const isSubmitting = form.formState.isSubmitting || isChecking;
  const isFormInvalid = !form.formState.isValid;

  return (
    <div className="bg-white  flex min-h-screen flex-col">
      {isRedirecting && (
        <div className="fixed inset-0 z-100 flex items-center justify-center bg-white/70">
          <Spinner className="size-8 text-primary" />
        </div>
      )}
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b bg-white px-4 md:px-6 py-4">
        <WaterlilyLogo className="h-6" />
        <Link
          to="/"
          className="text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
        >
          Already have an account?{' '}
          <span className="font-medium text-primary hidden sm:inline">Sign in</span>
          <span className="font-medium text-primary sm:hidden">Log in</span>
        </Link>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 pt-24 md:pt-20 pb-12">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="bg-primary-100 mb-3 flex h-10 w-10 items-center justify-center rounded-full transition-transform duration-300 hover:scale-110">
            <Shield className="text-primary h-5 w-5" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            {isPasswordMode ? 'Dev: Password Signup' : 'Quick security step'}
          </h1>
          <p className="mt-1 max-w-sm text-sm text-gray-500">
            {isPasswordMode
              ? 'Create an account with email and password (local dev only)'
              : "We'll verify your email and phone to protect you and your clients"}
          </p>
        </div>

        <Card className="w-full max-w-md border-0 shadow-none md:border md:border-gray-200 md:shadow-sm transition-shadow duration-200 hover:shadow-md bg-transparent md:bg-white">
          <CardContent className="p-0 md:p-8">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Mail className="h-4 w-4 text-gray-400" />
                        Email address
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          className="h-12 rounded-lg border-gray-200 focus:border-primary focus:ring-primary transition-all duration-200"
                          autoFocus
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!isPasswordMode && (
                  <FormField
                    control={form.control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <Phone className="h-4 w-4 text-gray-400" />
                          Phone number
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="tel"
                            placeholder="(555) 555-5555"
                            className="h-12 rounded-lg border-gray-200 focus:border-primary focus:ring-primary transition-all duration-200"
                            {...field}
                            onChange={handlePhoneNumberChange}
                          />
                        </FormControl>
                        <FormDescription className="text-xs text-gray-600">
                          Verification texts only. No spam.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {existingAdvisor && (
                  <Alert variant="warning" className="border-amber-200 bg-amber-50">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      {existingAdvisor.matchedBy === 'email' && (
                        <>An account with this email already exists.</>
                      )}
                      {existingAdvisor.matchedBy === 'phone' && (
                        <>An account with this phone number already exists.</>
                      )}
                      {existingAdvisor.matchedBy === 'both' && (
                        <>An account with this email and phone number already exists.</>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                {existingAdvisor ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-gray-200 hover:bg-gray-50 text-gray-700 font-medium py-6 rounded-lg transition-all duration-200"
                    asChild
                  >
                    <Link to="/" onClick={clearExistingAdvisorError}>
                      Sign in to your account
                    </Link>
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-primary-hover text-white font-medium py-6 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                    disabled={isSubmitting || isFormInvalid}
                  >
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isPasswordMode ? 'Continue with password' : 'Send verification code'}
                  </Button>
                )}

                <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-gray-200">
                  <span className="bg-white relative z-10 px-4 text-gray-500">
                    Or continue with
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-center gap-2 border-gray-200 hover:bg-gray-50 text-gray-700 font-medium py-6 rounded-lg transition-all duration-200"
                    onClick={() => handleSocialLogin('google-oauth2')}
                  >
                    {getOauthProviderIcon('google')}
                    Google
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-center gap-2 border-gray-200 hover:bg-gray-50 text-gray-700 font-medium py-6 rounded-lg transition-all duration-200"
                    onClick={() => handleSocialLogin('windowslive')}
                  >
                    {getOauthProviderIcon('microsoft')}
                    Outlook
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
